<script lang="ts">
    import dayjs from 'dayjs';
    import 'dayjs/locale/fr';
    import { createDateRangePicker, createTabs } from '@melt-ui/svelte';
    import { parseDate } from '@internationalized/date';
    import {
        VpCalendarIcon,
        VpReturnIcon,
        VpCheckIcon,
    } from '@decathlon-experience/icons/svelte';

    import { isDateSelectionFullMonth } from '$saga/views/Search/utils';
    import CalendarPicker from '$saga/components/CalendarPicker.svelte';
    import Button from '$saga/components/Button.svelte';
    import cn from '$common/utils/cn';
    import { filtersContext } from '$saga/views/Search/store';
    import FlexibilityFilter from './FlexibilityFilter.svelte';

    export let handleValidateSelection: () => void;
    export let overflowContainer: HTMLElement | null = null;

    const NUMBER_OF_MONTHS = 3;
    dayjs.locale('fr');

    const {
        elements: {
            calendar,
            cell,
            grid,
            heading,
            nextButton,
            prevButton,
            content,
        },
        states: { value, months, headingValue, weekdays },
        helpers: { isDateDisabled, isDateUnavailable },
    } = createDateRangePicker({
        locale: 'fr',
        minValue: parseDate(dayjs().add(1, 'day').format('YYYY-MM-DD')),
        // if endDateValue is present, then we chose a month
        defaultValue: {
            start: $filtersContext.formValues.startDate,
            end: $filtersContext.formValues.endDate,
        },
        forceVisible: true,
        numberOfMonths: NUMBER_OF_MONTHS,
        onValueChange: (dateValue) => {
            // Clear endDate when switching to specific date selection (date-selection)
            // This ensures month selection is cleared when user picks a specific date
            if ($valueTabs === 'date-selection') {
                $filtersContext.formValues.endDate = undefined;
            }

            return dateValue.next;
        },
    });

    const {
        elements: { root, list, content: contentTabs, trigger },
        states: { value: valueTabs },
    } = createTabs({
        defaultValue: 'month-selection',
    });
    const triggers = [
        { id: 'month-selection', title: 'Mois de départ' },
        { id: 'date-selection', title: 'Période de date de départ' },
    ];

    // set the initial value of valueTabs based on if startDateValue and endDateValue are set to first and last day of month
    $: {
        if (
            isDateSelectionFullMonth(
                $filtersContext.formValues.startDate,
                $filtersContext.formValues.endDate,
            )
        ) {
            $valueTabs = 'month-selection';
        } else {
            $valueTabs = 'date-selection';
        }
    }

    // remote validate the selection (e.g dialog is closed from outside click)
    export const validateSelection = () => {
        $filtersContext.formValues.startDate = $value.start;
        $filtersContext.formValues.endDate = $value.end;

        $filtersContext.formLabels.startDate = $value.start;
        $filtersContext.formLabels.endDate = $value.end;

        handleValidateSelection();
    };
</script>

<div
    {...$root}
    use:root
    class={cn(
        'sticky inset-x-0 top-0 z-50 flex w-full justify-center bg-white px-l py-s pt-s desktop:px-3xl',
    )}
>
    <div
        {...$list}
        use:list
        class="flex gap-m rounded-full bg-container-quiet p-xs"
    >
        {#each triggers as triggerItem (triggerItem.id)}
            <button
                {...$trigger}
                use:trigger
                class={cn(
                    'full rounded-full px-xs py-2xs text-xs text-content-brand transition-colors duration-300',
                    $valueTabs === triggerItem.id &&
                        'bg-container-brand text-white',
                )}
                on:click={() => ($valueTabs = triggerItem.id)}
            >
                {triggerItem.title}
            </button>
        {/each}
    </div>
</div>

<div {...$contentTabs('month-selection')} use:contentTabs class="flex-1">
    <div class="mx-auto mt-xl grid w-fit grid-cols-3 gap-2xl pb-xl">
        <!--    display the 12 next months as a button -->
        {#each Array(12) as _, index (index)}
            {@const month = dayjs().add(index, 'month')}
            <button
                class={cn(
                    'flex size-[80px] flex-col items-center justify-center gap-xs rounded-xl border border-solid border-border-neutral text-sm',
                    'hover:border-border-brand hover:bg-container-commercial-neutral hover:text-content-brand',
                    $filtersContext.formValues.endDate != null &&
                        $filtersContext.formValues.startDate &&
                        dayjs(
                            $filtersContext.formValues.startDate?.toString(),
                        ).get('month') === month.get('month') &&
                        'border-border-brand bg-container-brand text-white',
                )}
                on:click={() => {
                    // set formValues.startDate and formValues.endDate to the first day of the month and last day of the month: if the month is in the past, then select the month the next year
                    const startDate = month.startOf('month');
                    const endDate = month.endOf('month');
                    $filtersContext.formValues.startDate = parseDate(
                        startDate.format('YYYY-MM-DD'),
                    );
                    $filtersContext.formValues.endDate = parseDate(
                        endDate.format('YYYY-MM-DD'),
                    );
                    $filtersContext.formValues.flexibility = 0;

                    $filtersContext.formLabels.startDate =
                        $filtersContext.formValues.startDate;
                    $filtersContext.formLabels.endDate =
                        $filtersContext.formValues.endDate;

                    $value = {
                        start: $filtersContext.formValues.startDate,
                        end: $filtersContext.formValues.endDate,
                    };
                }}
            >
                <VpCalendarIcon class="size-xl" />
                {month.format('MMM YY')}
            </button>
        {/each}
    </div>
</div>

<div {...$contentTabs('date-selection')} use:contentTabs>
    <div {...content} use:content>
        <CalendarPicker
            {calendar}
            {prevButton}
            {heading}
            {headingValue}
            {nextButton}
            {months}
            {grid}
            {weekdays}
            {isDateDisabled}
            {isDateUnavailable}
            {cell}
            monthsOrientation="vertical"
            numberOfMonths={NUMBER_OF_MONTHS}
            {overflowContainer}
        />
    </div>
</div>

<div class="sticky bottom-0 left-0 w-full bg-white py-s">
    {#if $valueTabs === 'date-selection'}
        <FlexibilityFilter />
    {/if}

    <div
        class="flex w-full justify-between border-t border-solid border-border-quiet py-s"
    >
        <Button
            size="small"
            intent="secondary"
            shape="rounded"
            type="button"
            on:click={() => {
                $filtersContext.formValues.startDate = undefined;
                $filtersContext.formValues.endDate = undefined;
                $filtersContext.formValues.flexibility = 0;
                $filtersContext.formLabels.startDate = undefined;
                $filtersContext.formLabels.endDate = undefined;
                $value = { start: undefined, end: undefined };

                handleValidateSelection();
            }}
            startIcon={VpReturnIcon}>Effacer</Button
        >
        <Button
            size="small"
            shape="rounded"
            type="button"
            startIcon={VpCheckIcon}
            on:click={validateSelection}
        >
            Appliquer
        </Button>
    </div>
</div>

<!-- Override default CalendarPicker style to adapt to a DatePickerRange interaction -->
<style lang="postcss">
    :global([data-melt-calendar-cell][data-selected][data-selection-start]) {
        @apply bg-blue-brand text-white !important;
    }

    :global([data-melt-calendar-cell][data-selected][data-selection-end]) {
        @apply bg-blue-brand text-white !important;
    }

    :global([data-melt-calendar-cell][data-selected]) {
        @apply bg-container-commercial-neutral text-content-neutral !important;
    }

    :global(
        td:has(
            [data-melt-calendar-cell][data-selected]:not(
                    [data-selection-end]
                ):not([data-outside-month])
        )
    ) {
        @apply bg-container-commercial-neutral !important;
    }

    :global(
        td:has(
            [data-melt-calendar-cell][data-highlighted]:not(
                    [data-selection-end]
                ):not([data-outside-month])
        )
    ) {
        @apply bg-container-commercial-neutral !important;
    }

    :global(table) {
        @apply border-collapse !important;
    }
</style>
